'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@crabs-ai/ds-react';
import { useAnimationDropPreview } from './animation-drop-preview-provider';
import { getCurrentAnimationDragData } from '../editor-property-panel/ui/contents/animation/animation-drag-data';
import AnimationPreview from '../editor-property-panel/ui/contents/animation/AnimationPreview';

interface DragState {
  cursorPosition: { x: number; y: number };
}

// 드래그 오버레이 크기
const OVERLAY_WIDTH = 120;
const OVERLAY_HEIGHT = 87.5;

/**
 * 애니메이션 드래그 시 마우스를 따라다니는 커스텀 오버레이
 * 타겟(아이템)이 정해지면 숨겨지고, 타겟이 없으면 표시됨
 */
export const AnimationDragOverlay: React.FC = () => {
  const { previewState } = useAnimationDropPreview();
  const [dragState, setDragState] = useState<DragState | null>(null);

  // 전역 드래그 이벤트 리스닝
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      const animationData = getCurrentAnimationDragData();
      if (animationData) {
        setDragState({
          cursorPosition: { x: e.clientX, y: e.clientY },
        });
      }
    };

    const handleDragEnd = () => {
      setDragState(null);
    };

    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('drop', handleDragEnd);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('drop', handleDragEnd);
    };
  }, []);

  // 타겟이 정해지면 (previewState가 있으면) 숨김
  // 또는 dragState가 없으면 숨김
  if (!dragState || previewState) {
    return null;
  }

  const { cursorPosition } = dragState;

  // 드래그 중인 애니메이션 데이터 가져오기
  const animationData = getCurrentAnimationDragData();
  if (!animationData) {
    return null;
  }

  const { type, name } = animationData;

  return createPortal(
    <div
      className="flex flex-col items-start rounded-2xs bg-sys-bg-surface-primary p-5xs opacity-30"
      style={{
        position: 'fixed',
        top: cursorPosition.y - OVERLAY_HEIGHT / 2,
        left: cursorPosition.x - OVERLAY_WIDTH / 2,
        width: OVERLAY_WIDTH,
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: 0.9,
      }}
    >
      {/* 애니메이션 프리뷰 */}
      <div className={cn('aspect-[16/9] w-full overflow-hidden rounded-xs')}>
        <AnimationPreview
          type={type}
          progress={0}
        />
      </div>
      {/* 애니메이션 이름 */}
      <span className="p-4xs text-center text-caption-xsmall text-sys-cont-neutral-default">
        {name}
      </span>
    </div>,
    document.body,
  );
};
